import { useMemo, useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { LockKeyhole } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  ReportDateRangeControl,
  formatCurrency,
  formatInteger,
  formatPercent,
  useReportPeriod,
} from '@/components/organisms/analytics-reports';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useIsFieldHidden } from '@/hooks/usePermissions';
import { useSchedulerTechnicians } from '@/hooks/scheduler/useSchedulerTechnicians';
import { apiGet, operationsClient } from '@/lib/api';
import {
  operationalInsightEndpoints,
  targetEndpoints,
  technicianAnalyticsReportEndpoints,
} from '@/lib/api/endpoints';
import { operationalInsightKeys, targetKeys, technicianReportKeys } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';
import { useAuthTechnicianId } from '@/stores/auth.store';

export const Route = createFileRoute('/_dashboard/dashboard/reporting/me')({
  beforeLoad: () => {
    throw redirect({ to: '/dashboard/reporting/scorecards', replace: true });
  },
  component: MyPerformancePage,
});

interface PerformanceOverview {
  totalJobs: number;
  totalRevenue: number;
  totalHours: number;
  avgUtilization: number;
  revenuePerHour: number;
  membershipsSold: number;
}

interface PerformanceReport {
  overview: PerformanceOverview;
  overviewPrevious: PerformanceOverview;
  trend: { period: string; value: number }[];
}

interface SellingRow {
  technicianId: string;
  serviceCalls: number;
  opportunityGeneration: number;
  closeRate: number;
  soldPerOpportunity: number;
  callbackRate: number;
}

interface SellingReport {
  byTechnician: SellingRow[];
}

interface TargetRow {
  id: string;
  metric: string;
  scope: string;
  scopeId: string | null;
  targetValue: number;
  direction: 'HIGHER' | 'LOWER';
}

interface AttainmentRow {
  metric: string;
  actual: number;
  target: number;
  attainmentPct: number;
  targetMet: boolean;
}

interface AttainmentResponse {
  rows: AttainmentRow[];
}

interface MetricContract {
  key: string;
  label: string;
  unit: 'CURRENCY' | 'COUNT' | 'RATIO' | 'DAYS' | 'HOURS';
}

function MyPerformancePage() {
  const authTechnicianId = useAuthTechnicianId();
  const technicians = useSchedulerTechnicians();
  const pricingHidden = useIsFieldHidden('PRICING');
  const [selectedTechnicianId, setSelectedTechnicianId] = useState<string>();
  const period = useReportPeriod('analytics.reporting.my-performance', '30d');
  const technicianId = authTechnicianId ?? selectedTechnicianId ?? technicians.data?.[0]?.id ?? '';
  const technician = technicians.data?.find((item) => item.id === technicianId);
  const params = { from: period.from, to: period.to, technicianId };

  const performance = useApiQuery<PerformanceReport>({
    queryKey: technicianReportKeys.report('my-performance', params),
    queryFn: () =>
      apiGet<PerformanceReport>(
        operationsClient,
        technicianAnalyticsReportEndpoints.performance(params),
      ),
    enabled: Boolean(technicianId),
    showErrorToast: false,
  });
  const selling = useApiQuery<SellingReport>({
    queryKey: operationalInsightKeys.report(`my-performance-${technicianId}`, params),
    queryFn: () =>
      apiGet<SellingReport>(operationsClient, operationalInsightEndpoints.serviceCalls(params)),
    enabled: Boolean(technicianId),
    showErrorToast: false,
  });
  const targets = useApiQuery<TargetRow[]>({
    queryKey: targetKeys.list('TECHNICIAN', technicianId, period.to),
    queryFn: () =>
      apiGet<TargetRow[]>(
        operationsClient,
        targetEndpoints.list({
          periodStart: period.to,
          periodType: 'MONTH',
          scope: 'TECHNICIAN',
          scopeId: technicianId,
        }),
      ),
    enabled: Boolean(technicianId),
    showErrorToast: false,
  });
  const attainment = useApiQuery<AttainmentResponse>({
    queryKey: targetKeys.attainment('TECHNICIAN', technicianId, period.to),
    queryFn: () =>
      apiGet<AttainmentResponse>(
        operationsClient,
        targetEndpoints.attainment({
          reference: period.to,
          periodType: 'MONTH',
          scope: 'TECHNICIAN',
          scopeId: technicianId,
        }),
      ),
    enabled: Boolean(technicianId),
    showErrorToast: false,
  });
  const metrics = useApiQuery<MetricContract[]>({
    queryKey: [...targetKeys.all(), 'metrics'],
    queryFn: () => apiGet<MetricContract[]>(operationsClient, targetEndpoints.metrics()),
    showErrorToast: false,
  });

  const overview = performance.data?.overview;
  const sellingRow = selling.data?.byTechnician.find((row) => row.technicianId === technicianId);
  const metricByKey = useMemo(
    () => new Map((metrics.data ?? []).map((metric) => [metric.key, metric])),
    [metrics.data],
  );
  const attainmentByMetric = useMemo(
    () => new Map((attainment.data?.rows ?? []).map((row) => [row.metric, row])),
    [attainment.data],
  );
  const personalTargets = (targets.data ?? []).filter(
    (target) =>
      target.scope === 'TECHNICIAN' &&
      target.scopeId === technicianId &&
      (!pricingHidden || metricByKey.get(target.metric)?.unit !== 'CURRENCY'),
  );
  const loading =
    technicians.isLoading ||
    performance.isLoading ||
    selling.isLoading ||
    targets.isLoading ||
    attainment.isLoading;

  if (!technicianId && !technicians.isLoading) {
    return <NoTechnicianState />;
  }

  const periodLabel = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(period.to));

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-end gap-4 border-b bg-background px-6 py-4">
        <div className="min-w-60 flex-1">
          <p className="font-mono text-[9.5px] font-medium uppercase tracking-[0.15em] text-muted-foreground">
            {periodLabel} · {technician?.name ?? 'Technician'}
          </p>
          <h1 className="mt-1 text-xl font-semibold tracking-[-0.025em]">My performance</h1>
        </div>
        {!authTechnicianId ? (
          <Select value={technicianId} onValueChange={setSelectedTechnicianId}>
            <SelectTrigger className="w-52" size="sm">
              <SelectValue placeholder="Choose technician" />
            </SelectTrigger>
            <SelectContent>
              {(technicians.data ?? []).map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        <ReportDateRangeControl period={period} />
      </header>

      <main className="flex-1 overflow-auto p-6">
        <WebPerformanceView
          name={technician?.name ?? 'Technician'}
          periodLabel={periodLabel}
          overview={overview}
          previous={performance.data?.overviewPrevious}
          trend={performance.data?.trend ?? []}
          selling={sellingRow}
          targets={personalTargets}
          attainmentByMetric={attainmentByMetric}
          metricByKey={metricByKey}
          loading={loading}
          pricingHidden={pricingHidden}
        />
      </main>
    </div>
  );
}

function WebPerformanceView({
  name,
  periodLabel,
  overview,
  previous,
  trend,
  selling,
  targets,
  attainmentByMetric,
  metricByKey,
  loading,
  pricingHidden,
}: {
  name: string;
  periodLabel: string;
  overview?: PerformanceOverview;
  previous?: PerformanceOverview;
  trend: { period: string; value: number }[];
  selling?: SellingRow;
  targets: TargetRow[];
  attainmentByMetric: Map<string, AttainmentRow>;
  metricByKey: Map<string, MetricContract>;
  loading: boolean;
  pricingHidden: boolean;
}) {
  const jobs = overview?.totalJobs ?? 0;
  const revenue = overview?.totalRevenue ?? 0;
  const previousRevenue = previous?.totalRevenue ?? 0;
  const trendPoints = fourPeriodTrend(trend);
  const maxTrend = Math.max(1, ...trendPoints.map((point) => point.value));

  return (
    <div className="grid grid-cols-1 gap-[14px] lg:grid-cols-12">
      <section className="grid gap-6 rounded-[14px] border bg-card p-5 md:grid-cols-[1fr_auto] md:items-center lg:col-span-12">
        <div className="min-w-0">
          <Eyebrow>{periodLabel} · current period</Eyebrow>
          {loading ? (
            <Skeleton className="mt-4 h-9 w-80" />
          ) : (
            <h2 className="mt-4 text-[30px] font-medium leading-[1.08] tracking-[-0.04em] text-foreground">
              {jobs === 0
                ? 'No completed work in this period yet.'
                : pricingHidden
                  ? `${formatInteger(jobs)} completed jobs this period.`
                  : `${formatCurrency(revenue)} in completed job value.`}
            </h2>
          )}
          <p className="mt-3 text-[13px] leading-6 text-muted-foreground">
            {jobs === 0
              ? 'Results appear here as work is completed and hours are recorded.'
              : `${formatInteger(jobs)} completed jobs · ${formatInteger(overview?.totalHours ?? 0)} logged hours.`}
          </p>
        </div>
        <div className="flex max-w-72 items-center gap-3 rounded-[12px] bg-muted px-5 py-4">
          <LockKeyhole className="size-4 shrink-0 text-muted-foreground" />
          <p className="text-xs leading-5 text-foreground/80">
            Only {name} and reporting managers see this. Never a leaderboard.
          </p>
        </div>
      </section>

      <section className="rounded-[14px] border bg-card p-[18px] lg:col-span-7">
        <Eyebrow>Your targets</Eyebrow>
        <div className="mt-4 divide-y">
          {targets.length > 0 ? (
            targets.slice(0, 3).map((target) => {
              const result = attainmentByMetric.get(target.metric);
              const contract = metricByKey.get(target.metric);
              return (
                <TargetProgress
                  key={target.id}
                  label={contract?.label ?? target.metric}
                  actual={formatMetric(contract, result?.actual ?? 0)}
                  target={formatMetric(contract, target.targetValue)}
                  progress={result?.attainmentPct ?? 0}
                  met={result?.targetMet ?? false}
                  lowerIsBetter={target.direction === 'LOWER'}
                />
              );
            })
          ) : (
            <>
              <NoTargetMetric
                label="Completed job value"
                value={pricingHidden ? 'Hidden' : formatCurrency(revenue)}
              />
              <NoTargetMetric
                label="Labor utilization"
                value={formatPercent(overview?.avgUtilization)}
              />
              <NoTargetMetric label="Callback rate" value={formatPercent(selling?.callbackRate)} />
            </>
          )}
        </div>
      </section>

      <section className="flex min-h-[330px] flex-col rounded-[14px] border bg-card p-[18px] lg:col-span-5">
        <Eyebrow>You against you</Eyebrow>
        <p className="mt-2 text-xs text-muted-foreground">Logged hours, last four periods</p>
        <div className="mt-5 flex h-48 items-end gap-4">
          {trendPoints.map((point, index, points) => {
            const height = point.value > 0 ? Math.max(12, (point.value / maxTrend) * 100) : 4;
            const current = index === points.length - 1;
            return (
              <div
                key={point.period}
                className="flex h-full flex-1 flex-col items-center justify-end gap-2"
              >
                <span
                  className={cn(
                    'font-mono text-[10px]',
                    current ? 'text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {formatInteger(point.value)}h
                </span>
                <span
                  className={cn('w-full rounded-t-md', current ? 'bg-[#A9BC97]' : 'bg-[#E1E2D8]')}
                  style={{ height: `${height}%` }}
                />
                <span
                  className={cn(
                    'font-mono text-[9px] uppercase tracking-[0.12em]',
                    current ? 'text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {shortPeriod(point.period)}
                </span>
              </div>
            );
          })}
        </div>
        <p className="mt-auto pt-5 text-[12.5px] leading-5 text-foreground/80">
          {trend.some((point) => point.value > 0)
            ? 'A private comparison with your own recent work.'
            : 'The comparison begins after logged time is available.'}
        </p>
      </section>

      <section className="rounded-[14px] border bg-card p-[18px] lg:col-span-7">
        <Eyebrow>Your period so far</Eyebrow>
        <div className="mt-4 grid grid-cols-2 gap-y-5 sm:grid-cols-5">
          <PeriodMetric value={formatInteger(selling?.serviceCalls)} label="service calls" />
          <PeriodMetric value={formatInteger(jobs)} label="jobs completed" divided />
          <PeriodMetric
            value={formatPercent(selling?.opportunityGeneration)}
            label="opportunity gen"
            divided
          />
          <PeriodMetric
            value={pricingHidden ? 'Hidden' : formatCurrency(selling?.soldPerOpportunity)}
            label="per opportunity"
            divided
          />
          <PeriodMetric
            value={formatInteger(overview?.membershipsSold)}
            label="memberships"
            divided
          />
        </div>
      </section>

      <section className="rounded-[14px] border bg-card p-[18px] lg:col-span-5">
        <Eyebrow>Your history</Eyebrow>
        <h3 className="mt-4 text-[13px] font-medium tracking-[-0.02em]">
          {previousRevenue > 0 && !pricingHidden
            ? `${revenue >= previousRevenue ? 'Ahead of' : 'Behind'} your previous period.`
            : 'Target history starts when a period closes.'}
        </h3>
        <div className="mt-4 flex gap-2.5">
          {lastSixMonths().map((month, index) => (
            <div key={month} className="flex-1 text-center">
              <span
                className={cn('block h-7 rounded-lg', index === 5 ? 'bg-[#A9BC97]/40' : 'bg-muted')}
              />
              <span className="mt-2 block font-mono text-[9px] uppercase tracking-[0.1em] text-muted-foreground">
                {month}
              </span>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs leading-5 text-muted-foreground">
          Filled periods will show whether the target was met. Results freeze when the period ends.
        </p>
      </section>
    </div>
  );
}

function TargetProgress({
  label,
  actual,
  target,
  progress,
  met,
  lowerIsBetter,
}: {
  label: string;
  actual: string;
  target: string;
  progress: number;
  met: boolean;
  lowerIsBetter: boolean;
}) {
  return (
    <div className="py-5 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="flex flex-wrap items-baseline gap-2.5">
          <span className="text-[12.5px] text-foreground/80">{label}</span>
          <span className="text-[14.5px] font-medium tracking-[-0.025em]">{actual}</span>
          <span className="text-[12.5px] text-muted-foreground">
            {lowerIsBetter ? 'limit' : 'of'} {target}
          </span>
        </div>
        <span
          className={cn(
            'rounded-full px-2.5 py-1 text-[11px] font-medium',
            met ? 'bg-[#E8EDE5] text-[#2E4A2E]' : 'bg-muted text-muted-foreground',
          )}
        >
          {met ? (lowerIsBetter ? 'UNDER · GOOD' : 'TARGET MET') : formatPercent(progress)}
        </span>
      </div>
      <div className="mt-4 h-[11px] overflow-hidden rounded-full bg-muted">
        <span
          className={cn('block h-full rounded-full', met ? 'bg-[#A9BC97]' : 'bg-foreground/35')}
          style={{ width: `${Math.min(100, Math.max(0, progress * 100))}%` }}
        />
      </div>
    </div>
  );
}

function NoTargetMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="py-5 first:pt-0 last:pb-0">
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-2.5">
          <span className="text-[12.5px] text-foreground/80">{label}</span>
          <span className="text-[14.5px] font-medium tracking-[-0.025em]">{value}</span>
        </div>
        <span className="rounded-full bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
          NO TARGET SET
        </span>
      </div>
      <div className="mt-4 h-[11px] rounded-full bg-muted" />
    </div>
  );
}

function PeriodMetric({
  value,
  label,
  divided = false,
}: {
  value: string;
  label: string;
  divided?: boolean;
}) {
  return (
    <div className={cn('px-4 first:pl-0', divided && 'border-l')}>
      <p className="text-[17px] font-medium tracking-[-0.03em]">{value}</p>
      <p className="mt-1.5 text-xs leading-5 text-foreground/75">{label}</p>
    </div>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="font-mono text-[9.5px] font-medium uppercase tracking-[0.15em] text-muted-foreground">
      {children}
    </p>
  );
}

function NoTechnicianState() {
  return (
    <div className="flex h-full items-center justify-center p-6">
      <section className="w-full max-w-3xl rounded-[14px] border bg-card p-8">
        <Eyebrow>Private performance</Eyebrow>
        <h1 className="mt-4 text-[30px] font-medium tracking-[-0.04em]">
          Add a technician to begin.
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Personal results need a technician profile linked to scheduled and completed work.
        </p>
      </section>
    </div>
  );
}

function formatMetric(contract: MetricContract | undefined, value: number): string {
  if (contract?.unit === 'CURRENCY') return formatCurrency(value);
  if (contract?.unit === 'RATIO') return formatPercent(value);
  if (contract?.unit === 'HOURS') return `${formatInteger(value)}h`;
  if (contract?.unit === 'DAYS') return `${formatInteger(value)}d`;
  return formatInteger(value);
}

function shortPeriod(period: string): string {
  const match = /-(\d{2})$/.exec(period);
  if (!match) return period.slice(-3);
  const month = Number(match[1]);
  return new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' }).format(
    new Date(Date.UTC(2026, month - 1, 1)),
  );
}

function emptyTrend(): { period: string; value: number }[] {
  const now = new Date();
  return Array.from({ length: 4 }, (_, index) => {
    const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (3 - index), 1));
    return { period: date.toISOString().slice(0, 7), value: 0 };
  });
}

function fourPeriodTrend(
  trend: { period: string; value: number }[],
): { period: string; value: number }[] {
  const valueByPeriod = new Map(trend.map((point) => [point.period.slice(0, 7), point.value]));
  return emptyTrend().map((point) => ({
    period: point.period,
    value: valueByPeriod.get(point.period) ?? 0,
  }));
}

function lastSixMonths(): string[] {
  const now = new Date();
  return Array.from({ length: 6 }, (_, index) => {
    const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (5 - index), 1));
    return new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' }).format(date);
  });
}
